/**
 * Centralized API endpoints - single source of truth for all API paths.
 * Use these constants instead of hardcoded strings for easier maintenance.
 */

// Auth
export const AUTH = {
  PROFILE: '/auth/profile',
  LOGIN: '/auth/login',
  LOGOUT: '/auth/logout',
  PROFILE_UPDATE: '/auth/profile-update',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  MEETINGS: '/auth/meetings',
  /** multipart form field: onesignal_player_id (OneSignal push subscription id) */
  SAVE_PLAYER_ID: '/auth/save-player-id',
};

export const INVOICE_REFERENCE = {
  CURRENCIES: '/currencies',
  PAYMENT_TYPES: '/payment-types',
  ACCOUNTS: '/payment-accounts',
};

// Users
export const USERS = {
  LIST: '/users',
  BY_ID: (id) => `/users/${id}`,
  BY_ROLE: (role) => `/users/by-role?role=${role}`,
  DIRECT_PERMISSIONS: (userId) => `/users/${userId}/direct-permissions`,
  /** POST — exchange admin session for a token for the target user (impersonation). */
  IMPERSONATE: (id) => `/users/${id}/impersonate`,
  /** POST — end impersonation, restore the original (admin) session token. */
  IMPERSONATE_LEAVE: '/users/impersonate/leave',
};

/** Customers */
export const CUSTOMERS = {
  LIST: '/customers',
  BY_ID: (id) => `/customers/${id}`,
  ROLE_NAME: 'Customer',
};

// Roles
export const ROLES = {
  LIST: '/roles',
  BY_ID: (roleId) => `/roles/${roleId}`,
};

// Permissions
export const PERMISSIONS = {
  LIST: '/permissions?paginate=0',
};

// Statuses
export const STATUSES = {
  LIST: '/statuses',
  BY_ID: (id) => `/statuses/${id}`,
  /** Unpaginated list for dropdowns — always include paginate=0 */
  BY_MODEL: (model) =>
    `/statuses?conditions[model]=${encodeURIComponent(model)}&paginate=0`,
};

// Notifications
export const NOTIFICATIONS = {
  LIST: '/notifications',
  UNREAD: '/notifications/unread',
  BY_ID: (id) => `/notifications/${id}`,
  MARK_ALL_READ: '/notifications/mark-all-read',
};

// DBAs / Merchants
export const DBAS = {
  LIST: '/dbas',
  BY_ID: (id) => `/dbas/${id}`,
  PROCESS_STATUS: (id) => `/dbas/${id}/process-status`,
  STATUS: (id) => `/dbas/${id}/status`,
  REASSIGN: (id) => `/dbas/${id}/reassign`,
};

// Leads
export const LEADS = {
  LIST: '/leads',
  BY_ID: (id) => `/leads/${id}`,
  IMPORT: '/leads/import',
  UPDATE_STATUS: (id) => `/leads/${id}/update-status`,
  ASSIGN_USER: (id) => `/leads/${id}/assign-user`,
  ASSIGNED_USERS: (id) => `/leads/${id}/assigned-users`,
  ACTIVITY_LOGS: (id) => `/leads/${id}/activity-logs`,
  INITIATE: (id) => `/leads/${id}/initiate`,
  DOWNLOAD: '/leads/download',
  /** PUT body: { status, comment } — status: 1 = reject, 2 = resolve */
  UPDATE_ATTACHMENT_STATUS: (attachmentId) =>
    `/attachments/${attachmentId}/status`,
  /** POST — regenerate customer portal credentials */
  REGENERATE_CREDENTIALS: (id) => `/leads/${id}/regenerate-credentials`,
};

// Shareable links
export const SHAREABLE_LINKS = {
  CREATE: '/shareable-links',
};

// Issues
export const ISSUES = {
  LIST: '/issues',
  BY_ID: (id) => `/issues/${id}`,
  CREATE: '/issues',
  UPDATE_STATUS: (id) => `/issues/${id}/update-status`,
  ASSIGN_USER: (id) => `/issues/${id}/assign-user`,
  ASSIGNED_USERS: (id) => `/issues/${id}/assigned-users`,
};

// Teams
export const TEAMS = {
  BASE: '/teams',
  LIST: '/teams?relations[0]=members&relations[1]=status',
  BY_ID: (id) => `/teams/${id}`,
};

// Prospects
export const PROSPECTS = {
  LIST: '/prospects',
  BY_ID: (id) => `/prospects/${id}`,
  CONVERT: (id) => `/prospects/${id}/convert`,
  STATS: '/prospects/stats',
  SEARCH: '/prospects/search',
};

// Meetings
export const MEETINGS = {
  LIST: '/meetings',
  BY_ID: (id) => `/meetings/${id}`,
  UPDATE_STATUS: (id) => `/meetings/${id}/update-status`,
};

/** Call recordings — POST multipart (create metadata and/or upload file per backend contract) */
export const CALL_RECORDS = {
  LIST: '/call-records',
  CREATE: '/call-records',
  BY_ID: (id) => `/call-records/${id}`,
};

// Dashboard
export const DASHBOARD = {
  TOTAL: '/dashboards',
  /** GET /dashboards/admin?date_from=&date_to=&team_id= — teams + closers metrics */
  ADMIN: '/dashboards/admin',
  /** GET /dashboards/closer?date_from=&date_to= — closer personal metrics */
  CLOSER: '/dashboards/closer',
  /** GET /dashboards/calls_counter_summary?date_from=&date_to=&team_id= */
  CALLS_COUNTER_SUMMARY: '/reports/calls_counter_summary',
  /** GET /dashboards/leads_comments */
  LEADS_COMMENTS: '/reports/leads_comments',
};

// Activity logs
export const ACTIVITY_LOGS = {
  LIST: '/activity-logs',
  BY_ID: (id) => `/activity-logs/${id}`,
};

// Attachments / Documents
export const ATTACHMENTS = {
  LIST: '/attachments',
  BY_ID: (id) => `/attachments/${id}`,
  DOWNLOAD: (id) => `/attachments/${id}/download`,
  DOWNLOAD_BY_PATH: '/attachments/download',
};

// Lookup / Reference data
export const LOOKUP = {
  COUNTRIES: '/countries',
  COUNTRY_BY_ID: (id) => `/countries/${id}`,
  ATTRIBUTE_TYPES: '/attribute-types',
  EQUIPMENT_TYPES: '/equipment-types',
  EQUIPMENT_MODELS: '/equipment-models',
  MANUFACTURERS: '/manufacturers',
  BANKRUPT_TYPES: '/bankrupt-types',
  SOURCES: '/sources',
  BRANDS: '/brands',
  BUSINESS_CATEGORIES: '/business-categories?relations[0]=author&relations[1]=status',
};


export const Equipments = {
  LIST: '/equipments',
  GETEQUIPMENTDBA: () => `/equipments/merchant-equipments`,
  BY_ID: (id) => `/equipments/${id}`,
  STATUS: (id) => `/equipments/${id}/status`,
  EQUIPMENT_LOGS: (id) => `/equipments/${id}/equipment-logs`,
};

// E-Sign Templates (for Create Contract form)
export const E_SIGN_TEMPLATES = {
  LIST: '/e-sign-templates?relations[0]=status&relations[1]=author',
};

// Daily Summary (agent call reports) – GET list, POST create, GET/PUT/DELETE by id, POST import
export const DAILY_SUMMARY = {
  LIST: '/agent-call-reports',
  CREATE: '/agent-call-reports',
  BY_ID: (id) => `/agent-call-reports/${id}`,
  IMPORT: '/agent-call-reports/import',
  /** Assign numbers / dial-from numbers for dropdown */
  // ASSIGNED_NUMBERS: '/daily-summaries/assigned-numbers',
};

// E-Sign Contracts (listing, create, actions)
export const E_SIGN_CONTRACTS = {
  LIST: '/e-sign-contracts',
  CREATE: '/e-sign-contracts',
  BY_ID: (id) => `/e-sign-contracts/${id}`,
  /** POST /e-sign-contracts/:id/resend_sign_request?signer_api_id= */
  RESEND: (contractId, signerApiId) =>
    `/e-sign-contracts/${contractId}/resend_sign_request${signerApiId ? `?signer_api_id=${encodeURIComponent(signerApiId)}` : ''}`,
  /** GET /e-sign-contracts/:id/download?sign_page_url=... — backend fetches PDF from sign URL */
  DOWNLOAD: (id, signPageUrl) =>
    `/e-sign-contracts/${id}/download${signPageUrl ? `?sign_page_url=${encodeURIComponent(signPageUrl)}` : ''}`,
  SIGN_URL: (id) => `/e-sign-contracts/${id}/sign-url`,
};

export const QUICK_NOTES = {
  LIST: '/quick-notes',
  BY_ID: (id) => `/quick-notes/${id}`,
  MARK_AS_PIN: (id) => `/quick-notes/${id}/mark-as-pin`,
  UNMARK_AS_PIN: (id) => `/quick-notes/${id}/un-mark-as-pin`,
  MARK_AS_COMPLETE: (id) => `/quick-notes/${id}/mark-as-complete`,
  UNMARK_AS_COMPLETE: (id) => `/quick-notes/${id}/unmark-as-complete`,
};



// Brands — CRUD on /brands; form-data on POST/PUT (see brands/CreateBrand.jsx)
export const BRANDS = {
  LIST: '/brands',
  BY_ID: (id) => `/brands/${id}`,
};

// Services — CRUD on /services
export const SERVICES = {
  LIST: '/services',
  BY_ID: (id) => `/services/${id}`,
};

// Leases — CRUD on /leases
export const LEASES = {
  LIST: '/leases',
  BY_ID: (id) => `/leases/${id}`,
};

// Merchant service providers — CRUD on /merchant-service-providers
export const MERCHANT_SERVICE_PROVIDERS = {
  LIST: '/merchant-service-providers',
  BY_ID: (id) => `/merchant-service-providers/${id}`,
};

// Equipment models — CRUD on /equipment-models
export const EQUIPMENT_MODELS_RESOURCE = {
  LIST: '/equipment-models',
  BY_ID: (id) => `/equipment-models/${id}`,
};

// Companies — CRUD on /companies (multipart for logo where applicable)
export const COMPANIES = {
  LIST: '/companies',
  BY_ID: (id) => `/companies/${id}`,
};

// Task types — CRUD on /task-types (form-data on POST/PUT per backend)
export const TASK_TYPES = {
  LIST: '/task-types',
  BY_ID: (id) => `/task-types/${id}`,
};

// Task categories — CRUD on /task-categories (form-data on POST/PUT per backend)
export const TASK_CATEGORIES = {
  LIST: '/task-categories',
  BY_ID: (id) => `/task-categories/${id}`,
};

// Task templates — CRUD on /task-templates (form-data on POST/PUT per backend)
export const TASK_TEMPLATES = {
  LIST: '/task-templates',
  BY_ID: (id) => `/task-templates/${id}`,
};

// Tasks — CRUD on /tasks (form-data on POST/PUT per backend)
export const TASKS = {
  LIST: '/tasks',
  BY_ID: (id) => `/tasks/${id}`,
};

// Import businesses — list, view, import file (multipart files[])
export const IMPORT_BUSINESSES = {
  LIST: '/import-businesses',
  BY_ID: (id) => `/import-businesses/${id}`,
  IMPORT: '/import-businesses',
  SAMPLE_FILE: '/import-businesses/sample-file',
  /** POST form-data: _method=PUT, status_id, description */
  UPDATE_STATUS: (id) => `/import-businesses/${id}/status`,
  /** POST form-data: assignee_id, import_businesses[0], import_businesses[1], ... */
  ASSIGN_DIALING: '/import-businesses/assign-dialing-business',
};

// Task comments — GET/POST on /task-comments
export const TASK_COMMENTS = {
  LIST: '/task-comments',
  BY_ID: (id) => `/task-comments/${id}`,
};

/** Reference data for brand forms */
export const BRAND_REFERENCE = {
  ACCOUNTS: '/payment-accounts',
  CATEGORIES: '/brand-categories',
};